import React, { useRef, useEffect, useState } from 'react';
import '../css/ChatBody.css';
import Markdown from '../utils/markdown';
import LeaveRequestForm from './dynamicForms/LeaveRequestForm';
import LeaveQuota from './dynamicForms/LeaveQuota';
import LeaveTypes from './dynamicForms/LeaveTypes';
import PermissionTypes from './dynamicForms/PermissionTypes';
import PermissionRequestForm from './dynamicForms/PermissionRequestForm';
import ReturnFromLeave from './dynamicForms/ReturnFromLeave';
import UpdateLeave from './dynamicForms/UpdateLeave';
import { useTranslation } from 'react-i18next';
import HRCertificateForm from './dynamicForms/HRCertificates';
import EmployeesLeavesBalances from './dynamicForms/EmployeesLeavesBalances';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarDays, faClock, faChartPie, 
  faHouseLaptop, faEllipsisH, faReply, faUsers,
  faCalendarPlus,
  faCalendarMinus,
  faFileLines,
  faHeadset
} from '@fortawesome/free-solid-svg-icons';
import RatingBox from './RatingBox';
import ServiceRequestForm from './dynamicForms/ServiceRequestForm';
import { isMobile } from 'react-device-detect';

const ChatBody = ({ messages, language, onSendMessage, handleSend, disabled, addFirstMessage, setMessages, selectedOption }) => {
  const chatBodyRef = useRef(null);
  const [lastInteractiveMessageIndex, setLastInteractiveMessageIndex] = useState(null);
  const { t } = useTranslation();
  const [hiddenButtonsMessages, setHiddenButtonsMessages] = useState([]);
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && (lastMessage.isForm || !lastMessage.isForm)) {
      setLastInteractiveMessageIndex(messages.length - 1);
    }
  }, [messages]);

  useEffect(() => {
    const chatMessages = document.querySelectorAll('.chat-message');
    chatMessages.forEach((msg, index) => {
      setTimeout(() => {
        msg.classList.add('show');
      }, 100); // Stagger the animation for each message
    });
  }, [messages]);

  const scrollToBottom = () => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);

    if (isNaN(date.getTime())) {
      console.error('Invalid timestamp:', timestamp);
      return 'Invalid Time';
    }

    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const iconMapping = {
    leaves: "/leaves.svg",
    permissions: "/permission.svg",
    leave_balance: "/leaves.svg",
    apply_leave: "/request-leaves.svg",
    update_leave: "/leaves.svg",
    return_from_leave: "/request-leave-work.svg",
    remote_work: "/work-remote.svg",
    hr_certificates: "/cert.svg",
    show_more: "/show-more.svg",
    back: "/back-icon.svg",
    employees_leave_balances: "/leaves-count.svg",
    raise_support_ticket: "/support-ticket.svg"
  };  

  // Function to handle link clicks in plain text messages (Enable now links clicks).
  const handleLinkClick = (event, msg) => {
    // Check if the click occurred on an anchor element.
    if (event.target.tagName.toLowerCase() === 'a') {
      let frontendLog = JSON.stringify({
        sessionID: sessionStorage.getItem('sessionID'),
        employeeID: localStorage.getItem('EmployeeID'),
        channel: isMobile ? 'Mobile' : 'Portal',
        language: localStorage.getItem('language'),
      });

      const payload = {
        messageID: msg.messageID,
        serviceName: msg.custom,
        frontendLog: frontendLog,
      };
      if (event.target && event.target.getAttribute('href')) {
        const href = event.target.getAttribute('href');
    
        // Do not handle click if the href contains tel:185, this is another link to call contact center (Should not be handled as log)
        if (href.includes('tel:185')) {
          return;
        }

        // Make the API call to log the link click.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/chatbot/logLinkClick?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        .then(response => {
        })
        .catch(error => {
          console.error('Error logging link click:', error);
        });
      }
    }
  };

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    // List of all form names that should trigger a rating, Forms which not contains actions (The listing tables)
    const ratingTriggerForms = [
      "GetRelevantInfo",
      "LeaveBalanceForm",
      "EmployeesLeavesBalances",
      "GetHRLawRelevantInfo"
    ];
    
    if (lastMessage && ratingTriggerForms.includes(lastMessage.formName)) {
      const timer = setTimeout(() => {
        onSendMessage({
          sender: 'bot',
          isRating: true,
          requestType: "success",
          timestamp: new Date().getTime(),
        });
      }, (process.env.REACT_APP_TIME_TO_DISPLAY_REVIEW * 1000));
      return () => clearTimeout(timer);
    }
  }, [messages, onSendMessage]);

  const renderComponent = (msg, index) => {
    const isFormDisabled = lastInteractiveMessageIndex !== null && index < lastInteractiveMessageIndex;
    const areButtonsHidden = hiddenButtonsMessages.includes(index);
    const direction = language === 'ar' ? 'rtl' : 'ltr';
  
    const time = formatTime(msg.timestamp);
  
    if (msg.isForm && msg.formName === "LeaveRequestForm") {
      return (
        <LeaveRequestForm
          leaveRequestData={msg.text}
          language={language}
          isFormDisabled={isFormDisabled}
          onSendMessage={onSendMessage}
        />
      );
    } else if (msg.isForm && msg.formName === "PermissionRequestForm") {
      return (
        <PermissionRequestForm
          language={language}
          permissionRequestData={msg.text}
          isFormDisabled={isFormDisabled}
          onSendMessage={onSendMessage}
        />
      );
    } else if (msg.isForm && msg.formName === "LeaveBalanceForm") {
      return <LeaveQuota data={msg.text} language={language} onSendMessage={onSendMessage} />;
    } else if (msg.isForm && msg.formName === "LeaveTypes") {
      return <LeaveTypes data={msg.text} language={language} />;
    } else if (msg.isForm && msg.formName === "PermissionTypes") {
      return <PermissionTypes data={msg.text} language={language} />;
    } else if (msg.isForm && msg.formName === "ReturnFromLeave") {
      return <ReturnFromLeave data={msg.text} language={language} onSendMessage={onSendMessage} isDisabled={isFormDisabled} />;
    } else if (msg.isForm && msg.formName === "UpdateLeave") {
      return <UpdateLeave data={msg.text} onSendMessage={onSendMessage} language={language} isDisabled={isFormDisabled} />;
    } else if (msg.isForm && msg.formName === "RequestHRCertificate") {
      return <HRCertificateForm onSendMessage={onSendMessage} data={msg.text} language={language} isDisabled={isFormDisabled} />;
    } else if (msg.isForm && msg.formName === "EmployeesLeavesBalances") {
      return <EmployeesLeavesBalances data={msg.text} language={language} onSendMessage={onSendMessage} />;
    } else if (msg.isForm && msg.formName === "SupportTicketForm") {
      return <ServiceRequestForm data={msg.text} language={language} onSendMessage={onSendMessage} isDisabled={isFormDisabled} />;
    } else if (msg.isRating) {
      // Array of form names for which the immediate previous message should be used.
      const immediateFormArray = ['LeaveBalanceForm', 'EmployeesLeavesBalances', 'GetRelevantInfo', 'GetHRLawRelevantInfo']; // Add other form names as needed. The forms which the rating box will be attached on them.
      let previousMessage = null;
      if (index > 0) {
        // If the immediate previous message's formName is in our array, use that one (index - 1)
        if (
          messages[index - 1] &&
          messages[index - 1].formName &&
          immediateFormArray.includes(messages[index - 1].formName)
        ) {
          previousMessage = messages[index - 1];
        } else if (index - 2 >= 0) {
          // Otherwise (for the other forms like Leave request, permission request, ..etc), default to using the message at index - 2 (The rating will be on the confirmation message component).
          previousMessage = messages[index - 2];
        }
      }
      return (
        <RatingBox
          data={msg}
          previousMsg={previousMessage}
          scrollToBottom={scrollToBottom}
        />
      );
    } else if (msg.buttons) {
      return (
        <>
        <div className='leave-list-action-sender' style={{ direction }}>
          <div className='leave-balance-container'>
            <ul className='leave-balance-list'>
            {(!areButtonsHidden && !isFormDisabled) && msg.buttons.map((button, idx) => (
              <li className={`leave-balance-item`}
                key={idx} 
                onClick={() => handleButtonClick(index, button.value)}
                disabled={isFormDisabled}
              >
                <img 
                  src={iconMapping[button.value]} 
                  alt={button.value} 
                  className="w-8 h-8"
                />
                {button.value != 'back' && <span className={language == 'ar' ? 'widget-label' : 'widget-label-en'}>{button.label}</span>}
              </li>
            ))}
            </ul>
          </div>
        </div>
        </>
      );
    } else {
      if (msg.clearHistory) {
        sessionStorage.setItem('clearHistory', 'true');
      }
      // For plain text messages (which are not forms), attach the link click handler to log the Enable Now links clicks.
      return (
        <div style={{ direction }} onClick={(e) => handleLinkClick(e, msg)}>
          <Markdown content={msg.text} />
        </div>
      );
    }
  };

  const resetChatbotStatus = () => {
    setMessages([])
      // onSendMessage({
      //   sender: 'bot',
      //   text: t("Hi, I'm TAMM, your digital assistant. I can help you with Human Resources services."),
      //   type: "welcome"
      // });
  }

  const handleButtonClick = (msgIndex, value) => {
    if (value === 'leaves') {
      onSendMessage({
        sender: 'bot',
        text: "",
        type: "welcome",
        buttons: [
          { label: t('Leave Balance'), value: 'leave_balance' },
          { label: t('Apply Leave'), value: 'apply_leave' },
          { label: t('Update Leave'), value: 'update_leave' },
          { label: t('Return From Leave'), value: 'return_from_leave' },
          { label: t('Show less'), value: 'back', isNew: false },
        ]
      });
    } else if (value === 'permissions') {
      resetChatbotStatus();
      handleSend(t('Permission request'));
    } else if (value == "show_more") {
      const isManager = localStorage.getItem('IsManager') === 'true';
      // Manager allowed services
      if (isManager) {
        onSendMessage({
          sender: 'bot',
          text: "",
          type: "welcome",
          buttons: [
            { label: t('Employees Leave Balances'), value: 'employees_leave_balances', isNew: false },
            { label: t('Show less'), value: 'back', isNew: false },
          ]
        });
      }
    } else if (value == "back") {
      setMessages([])
      addFirstMessage()
    } else if (value === 'hr_certificates') {
      resetChatbotStatus();
      handleSend(t('HR Certificate request'));
    } else if (value === 'remote_work') {
      resetChatbotStatus();
      handleSend(t('Remote Work'));
    } else if (value === 'leave_balance') {
      resetChatbotStatus();
      handleSend(t('Leave balance'));
    } else if (value === 'apply_leave') {
      resetChatbotStatus();
      handleSend(t('Leave request'));
    } else if (value === 'update_leave') {
      resetChatbotStatus();
      handleSend(t('Update Leave request'));
    } else if (value === 'return_from_leave') {
      resetChatbotStatus();
      handleSend(t('Return From Leave request'));
    } else if (value === 'employees_leave_balances') {
      resetChatbotStatus();
      handleSend(t('Employees Leave Balances'));
    } else if (value === 'raise_support_ticket') {
      resetChatbotStatus();
      handleSend(t('Raise a Ticket'));
      localStorage.setItem('newShown', true); // If new service used, to disappear the new feature badge.
    }
  };

  return (
    <div className="chat-body" ref={chatBodyRef}>
      {messages.map((msg, index) => (
       <div
       key={index}
       className={`chat-message ${(msg.sender === "bot" && msg.type === "welcome") ? 'leave-list-sender' : msg.sender} ${
         (msg.formName && (msg.formName !== 'GetRelevantInfo' || msg.formName !== 'GetHRLawRelevantInfo') || msg.type === "welcome") ? 'w-100' : ''
       } ${
         msg.isRating ? 'rating-border': ''}`}>         
          <div className={`${(msg?.formName === "LeaveBalanceForm") ? 'leave-balance-container' : 'message-content'} ${(msg?.formName === "LeaveRequestForm" || msg?.formName === 'RequestHRCertificate' || msg?.formName === 'UpdateLeave') || msg?.formName === 'ReturnFromLeave' || msg?.formName === 'EmployeesLeavesBalances' ? 'leave-request-form' : ''|| msg?.formName === 'PermissionRequestForm'? 'leave-request-form' : '' ||  msg?.formName === 'SupportTicketForm'? 'leave-request-form' : ''}`}>
            {renderComponent(msg, index)}
          </div>
          {!msg.isRating && <div className={`message-time ${language === 'ar' ? 'time-left' : 'time-right'}`}>
            {formatTime(msg.timestamp)}
          </div>}
        </div>
      ))}
    </div>
  );
};

export default ChatBody;
